import { useState, useEffect } from "react";
import { Sparkles, Target, RefreshCw, BookOpen, Loader2 } from "lucide-react";
import { useAITutor, type ModuleRemediationData } from "@/hooks/useAITutor";

const FALLBACK: ModuleRemediationData = {
  mastery_gap_summary: "You're close to mastering this module. A few key concepts need more attention before you'll be ready.",
  likely_problem_areas: ["Core terminology and definitions", "Applying concepts to realistic scenarios", "Understanding tradeoffs and decisions"],
  retry_strategy: "Review the related course lessons, then come back and retry with fresh understanding.",
  infographic_text: "Focus on understanding the 'why' behind each concept, not just memorizing facts.",
  encouragement: "Every attempt teaches you something new. You're getting closer to mastery!",
};

interface Props {
  moduleKey: string;
  moduleName: string;
  score: number;
  maxScore: number;
  wrongQuestions?: { question: string; correctAnswer: string }[];
}

export default function AIModuleCoach({ moduleKey, moduleName, score, maxScore, wrongQuestions }: Props) {
  const { loading, getModuleRemediation } = useAITutor();
  const [data, setData] = useState<ModuleRemediationData | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      const result = await getModuleRemediation({ moduleKey, moduleName, score, maxScore, wrongQuestions });
      if (cancelled) return;
      if (result.fallback || !result.data) {
        setUseFallback(true);
      } else {
        setData(result.data);
      }
    };
    fetch();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, []);

  const content = data || (useFallback ? FALLBACK : null);

  return (
    <div className="rounded-2xl border border-secondary/20 bg-card/90 backdrop-blur-md p-5 space-y-4 shadow-[0_0_30px_hsl(var(--secondary)/0.1)]">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-secondary" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-sm text-foreground">AI Module Coach</h3>
          <p className="text-[10px] text-muted-foreground">Personalized coaching for your retry</p>
        </div>
      </div>

      {loading && !content ? (
        <div className="flex items-center justify-center py-6 gap-2">
          <Loader2 className="w-5 h-5 text-secondary animate-spin" />
          <span className="text-sm text-muted-foreground">Analyzing your performance...</span>
        </div>
      ) : content ? (
        <>
          <p className="text-sm text-muted-foreground leading-relaxed">{content.mastery_gap_summary}</p>

          <div className="bg-secondary/5 border border-secondary/15 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5 text-secondary" />
              <span className="text-xs font-semibold text-secondary">Focus Areas</span>
            </div>
            <ul className="space-y-1">
              {content.likely_problem_areas.map((area, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-secondary mt-0.5">•</span>
                  <span>{area}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-primary/5 border border-primary/15 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary">Retry Strategy</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.retry_strategy}</p>
          </div>

          <div className="bg-accent/5 border border-accent/15 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <BookOpen className="w-3.5 h-3.5 text-accent" />
              <span className="text-xs font-semibold text-accent">Quick Review</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed italic">{content.infographic_text}</p>
          </div>

          <p className="text-center text-xs text-muted-foreground/80 italic">{content.encouragement}</p>
        </>
      ) : null}
    </div>
  );
}
